/**
 * @module payments
 * @description Public payment-provider adapter contract surface. Runtime
 * payment implementations live in provider packages; this subpath exports only
 * the interface, Zod schemas, and thin error class.
 */

import type {
  AdapterHealth as SharedAdapterHealth,
  CheckoutParams as SharedCheckoutParams,
  CheckoutResult as SharedCheckoutResult,
  PaymentCheckoutInput as SharedPaymentCheckoutInput,
  PaymentCheckoutSession as SharedPaymentCheckoutSession,
  PaymentPlanContract as SharedPaymentPlanContract,
  PaymentSubscriptionContract as SharedPaymentSubscriptionContract,
  PaymentsAdapter as SharedPaymentsAdapter,
  PaymentsProviderAdapter as SharedPaymentsProviderAdapter,
  Plan as SharedPlan,
  Subscription as SharedSubscription,
} from "@geenius/adapters";
import {
  AdapterHealthSchema as SharedAdapterHealthSchema,
  PaymentCheckoutInputSchema as SharedPaymentCheckoutInputSchema,
  PaymentCheckoutSessionSchema as SharedPaymentCheckoutSessionSchema,
  PaymentPlanContractSchema as SharedPaymentPlanContractSchema,
  PaymentSubscriptionContractSchema as SharedPaymentSubscriptionContractSchema,
  PaymentsContractError as SharedPaymentsContractError,
} from "@geenius/adapters";

/**
 * Health result returned by payments providers after checking checkout,
 * subscription, and feature-gate availability.
 *
 * @example
 * ```ts
 * const health: AdapterHealth = await payments.getHealth();
 * ```
 */
export type AdapterHealth = SharedAdapterHealth;

/**
 * Legacy checkout request accepted by the root `PaymentsAdapter` contract.
 *
 * @example
 * ```ts
 * const params: CheckoutParams = { planId: "pro", userId: "user_1" };
 * ```
 */
export type CheckoutParams = SharedCheckoutParams;

/**
 * Legacy checkout response returned by the root `PaymentsAdapter` contract.
 *
 * @example
 * ```ts
 * const result: CheckoutResult = { url: "https://pay.example.com/checkout", sessionId: "cs_1" };
 * ```
 */
export type CheckoutResult = SharedCheckoutResult;

/**
 * Provider-neutral checkout creation payload accepted by payments providers.
 *
 * @example
 * ```ts
 * const input: PaymentCheckoutInput = { planId: "pro", identityId: "user_1" };
 * ```
 */
export type PaymentCheckoutInput = SharedPaymentCheckoutInput;

/**
 * Provider-neutral checkout session record returned after checkout creation.
 *
 * @example
 * ```ts
 * const session: PaymentCheckoutSession = {
 *   id: "cs_1",
 *   planId: "pro",
 *   identityId: "user_1",
 *   url: "https://pay.example.com/checkout",
 *   status: "created",
 *   createdAt: new Date().toISOString(),
 * };
 * ```
 */
export type PaymentCheckoutSession = SharedPaymentCheckoutSession;

/**
 * Payment plan record used by provider adapters for plan catalog responses.
 *
 * @example
 * ```ts
 * const plan: PaymentPlanContract = { id: "pro", name: "Pro", currency: "USD", amount: 2000, interval: "month", features: ["exports"] };
 * ```
 */
export type PaymentPlanContract = SharedPaymentPlanContract;

/**
 * Subscription lifecycle record returned by payments provider adapters.
 *
 * @example
 * ```ts
 * const subscription: PaymentSubscriptionContract = {
 *   id: "sub_1",
 *   planId: "pro",
 *   identityId: "user_1",
 *   status: "active",
 *   currentPeriodStart: new Date().toISOString(),
 *   currentPeriodEnd: new Date().toISOString(),
 *   cancelAtPeriodEnd: false,
 *   createdAt: new Date().toISOString(),
 *   updatedAt: new Date().toISOString(),
 *   metadata: {},
 * };
 * ```
 */
export type PaymentSubscriptionContract = SharedPaymentSubscriptionContract;

/**
 * Root payments adapter contract used by legacy local/dev and UI bindings.
 *
 * @example
 * ```ts
 * const plans = await payments.getPlans();
 * ```
 */
export type PaymentsAdapter = SharedPaymentsAdapter;

/**
 * SDK-free payments provider contract implemented by downstream provider
 * packages and app-owned billing modules.
 *
 * @example
 * ```ts
 * const checkout = await provider.createCheckout({ planId: "pro", identityId: "user_1" });
 * ```
 */
export type PaymentsProviderAdapter = SharedPaymentsProviderAdapter;

/**
 * Legacy plan record returned by the root `PaymentsAdapter.getPlans()` method.
 *
 * @example
 * ```ts
 * const plan: Plan = { id: "pro", name: "Pro", price: 2000, currency: "USD", interval: "month", features: ["exports"] };
 * ```
 */
export type Plan = SharedPlan;

/**
 * Legacy subscription record returned by the root payments adapter contract.
 *
 * @example
 * ```ts
 * const subscription: Subscription = { id: "sub_1", userId: "user_1", planId: "pro", status: "active", currentPeriodEnd: new Date().toISOString(), cancelAtPeriodEnd: false };
 * ```
 */
export type Subscription = SharedSubscription;

/**
 * Runtime validator for the common payments adapter health response.
 *
 * @example
 * ```ts
 * const health = AdapterHealthSchema.parse({ ok: true, checkedAt: new Date().toISOString() });
 * ```
 */
export const AdapterHealthSchema = SharedAdapterHealthSchema;

/**
 * Runtime validator for provider-neutral checkout creation payloads.
 *
 * @example
 * ```ts
 * const input = PaymentCheckoutInputSchema.parse({ planId: "pro", identityId: "user_1" });
 * ```
 */
export const PaymentCheckoutInputSchema = SharedPaymentCheckoutInputSchema;

/**
 * Runtime validator for provider-neutral checkout session records.
 *
 * @example
 * ```ts
 * const session = PaymentCheckoutSessionSchema.parse({ id: "cs_1", planId: "pro", identityId: "user_1", url: "https://pay.example.com/checkout", status: "created", createdAt: new Date().toISOString() });
 * ```
 */
export const PaymentCheckoutSessionSchema = SharedPaymentCheckoutSessionSchema;

/**
 * Runtime validator for payment plan contract records.
 *
 * @example
 * ```ts
 * const plan = PaymentPlanContractSchema.parse({ id: "pro", name: "Pro", currency: "USD", amount: 2000, interval: "month" });
 * ```
 */
export const PaymentPlanContractSchema = SharedPaymentPlanContractSchema;

/**
 * Runtime validator for subscription lifecycle records.
 *
 * @example
 * ```ts
 * const subscription = PaymentSubscriptionContractSchema.parse({ id: "sub_1", planId: "pro", identityId: "user_1", status: "active", currentPeriodStart: new Date().toISOString(), currentPeriodEnd: new Date().toISOString(), createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() });
 * ```
 */
export const PaymentSubscriptionContractSchema =
  SharedPaymentSubscriptionContractSchema;

/**
 * Contract-layer error for invalid payments payloads or provider-boundary
 * violations before a concrete payments SDK receives the request.
 *
 * @example
 * ```ts
 * throw new PaymentsContractError("Invalid checkout session");
 * ```
 */
export const PaymentsContractError = SharedPaymentsContractError;
